import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";

import { apiRequest } from "../../../common/api/apiClient.js";
import type {
  CreateShareRequest,
  DownloadTicketDTO,
  FileNodeDTO,
  FileSharesDTO,
  FolderContentDTO,
  ShareDTO,
  UploadTicketDTO,
} from "../../../common/api/types.js";

export const folderQueryKey = (folderId: string | null) => ["files", "folder", folderId] as const;
export const sharedWithMeQueryKey = ["files", "shared-with-me"] as const;
export const fileSharesQueryKey = (nodeId: string) => ["files", "shares", nodeId] as const;

export function useFolder(folderId: string | null): UseQueryResult<FolderContentDTO, Error> {
  return useQuery({
    queryKey: folderQueryKey(folderId),
    queryFn: () =>
      apiRequest<FolderContentDTO>(`/files${folderId ? `?folderId=${encodeURIComponent(folderId)}` : ""}`),
  });
}

export function useSharedWithMe(): UseQueryResult<FileNodeDTO[], Error> {
  return useQuery({
    queryKey: sharedWithMeQueryKey,
    queryFn: () => apiRequest<FileNodeDTO[]>("/files/shared-with-me"),
  });
}

export function useFileShares(nodeId: string | null): UseQueryResult<FileSharesDTO, Error> {
  return useQuery({
    queryKey: fileSharesQueryKey(nodeId ?? ""),
    queryFn: () => apiRequest<FileSharesDTO>(`/files/${nodeId as string}/shares`),
    enabled: nodeId !== null,
  });
}

export function useCreateFolder(folderId: string | null) {
  const client = useQueryClient();
  return useMutation<FileNodeDTO, Error, string>({
    mutationFn: (name) =>
      apiRequest<FileNodeDTO>("/files/folders", {
        method: "POST",
        body: folderId ? { name, parentId: folderId } : { name },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: folderQueryKey(folderId) });
    },
  });
}

/**
 * Lädt eine Datei hoch: Ticket beim Backend holen, Inhalt direkt in den Objektspeicher
 * schicken, danach bestätigen. Der Inhalt läuft nie durch die API.
 */
export function useUploadFile(folderId: string | null) {
  const client = useQueryClient();
  return useMutation<FileNodeDTO, Error, File>({
    mutationFn: async (file) => {
      const ticket = await apiRequest<UploadTicketDTO>("/files/uploads", {
        method: "POST",
        body: {
          name: file.name,
          mimeType: file.type || "application/octet-stream",
          sizeBytes: file.size,
          ...(folderId ? { parentId: folderId } : {}),
        },
      });

      const response = await fetch(ticket.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/octet-stream" },
      });
      if (!response.ok) {
        throw new Error("Die Datei konnte nicht in den Speicher geschrieben werden.");
      }

      return apiRequest<FileNodeDTO>(`/files/${ticket.nodeId}/confirm`, { method: "POST" });
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: folderQueryKey(folderId) });
    },
  });
}

/** Holt einen kurzlebigen Link und öffnet ihn — der Browser lädt direkt vom Speicher. */
export function useDownloadFile() {
  return useMutation<DownloadTicketDTO, Error, string>({
    mutationFn: (nodeId) => apiRequest<DownloadTicketDTO>(`/files/${nodeId}/download`),
    onSuccess: (ticket) => {
      window.location.assign(ticket.downloadUrl);
    },
  });
}

export function useRenameNode(folderId: string | null) {
  const client = useQueryClient();
  return useMutation<FileNodeDTO, Error, { nodeId: string; name: string }>({
    mutationFn: ({ nodeId, name }) =>
      apiRequest<FileNodeDTO>(`/files/${nodeId}/name`, { method: "PUT", body: { name } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: folderQueryKey(folderId) });
    },
  });
}

export function useTrashNode(folderId: string | null) {
  const client = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (nodeId) => apiRequest<void>(`/files/${nodeId}`, { method: "DELETE" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: folderQueryKey(folderId) });
      void client.invalidateQueries({ queryKey: sharedWithMeQueryKey });
    },
  });
}

export function useShareFile(nodeId: string | null) {
  const client = useQueryClient();
  return useMutation<ShareDTO, Error, CreateShareRequest>({
    mutationFn: (body) =>
      apiRequest<ShareDTO>(`/files/${nodeId as string}/shares`, { method: "POST", body }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: fileSharesQueryKey(nodeId ?? "") });
    },
  });
}

export function useRevokeFileShare(nodeId: string | null) {
  const client = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (shareId) =>
      apiRequest<void>(`/files/${nodeId as string}/shares/${shareId}`, { method: "DELETE" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: fileSharesQueryKey(nodeId ?? "") });
    },
  });
}
